/**
 * CBSWizardModal — Colorado "Contract to Buy & Sell Real Estate" (CBS) offer wizard.
 *
 * Rendered exclusively for CO transactions (txnState === 'CO') in TransactionTimeline.
 * Shares the same backend rpa-wizard API endpoints and the same prop contract as
 * RPAWizardModal so the TransactionTimeline infrastructure requires no structural change.
 *
 * Visual design: imports RPAWizardModal.css to inherit the full RPA shell (overlay,
 * modal, stepper, field, button styles) and adds CO-specific styles from CBSWizardModal.css.
 */

import React, { useState, useEffect, useRef } from 'react';
import './RPAWizardModal.css';
import './CBSWizardModal.css';
import { auth } from '../../firebase/config';
import { API_ENDPOINTS, apiClient } from '../../config/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const CBS_TOTAL_STEPS = 11;

const CBS_STEPS = [
  { id: 1,  label: 'Step 1',  title: 'Property Details' },
  { id: 2,  label: 'Step 2',  title: 'Parties' },
  { id: 3,  label: 'Step 3',  title: 'Price & EMD' },
  { id: 4,  label: 'Step 4',  title: 'Financing' },
  { id: 5,  label: 'Step 5',  title: 'Deadlines' },
  { id: 6,  label: 'Step 6',  title: 'Inclusions' },
  { id: 7,  label: 'Step 7',  title: 'Water Rights' },
  { id: 8,  label: 'Step 8',  title: 'HOA & Title' },
  { id: 9,  label: 'Step 9',  title: 'Disclosures' },
  { id: 10, label: 'Step 10', title: 'Addenda & Misc' },
  { id: 11, label: 'Step 11', title: 'Review & Confirm' },
];

// Inline SVGs matching each step's Tabler icon concept
const STEP_ICONS = {
  1:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>,
  2:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="3"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.87"/></svg>,
  3:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  4:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,
  5:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  6:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><polyline points="3 6 4 7 6 5"/><polyline points="3 12 4 13 6 11"/><polyline points="3 18 4 19 6 17"/></svg>,
  7:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" opacity=".3"/><path d="M12 7v5l4 2"/><circle cx="12" cy="12" r="3"/></svg>,
  8:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/></svg>,
  9:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  10: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  11: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
};

const CBS_STANDARD_INCLUSIONS = [
  'Lighting fixtures', 'Heating units', 'Plumbing', 'A/C units',
  'Floor coverings', 'Built-in appliances', 'Sprinkler systems',
  'Garage door openers', 'Interior wiring/jacks', 'Plants / mirrors',
];

const CBS_SELLER_OPT_INCLUSIONS = [
  'Solar Panels (owned)', 'Water Softener (owned)',
  'Security System (owned)', 'Satellite System (owned)',
];

const CBS_INITIAL_DATA = {
  // Step 1 — Property
  propertyAddress: '', city: '', state: 'CO', zip: '',
  county: '', legalDescription: '', propertyType: 'SFR',
  mlsNumber: '', listPrice: '',
  // Step 2 — Parties
  buyer1Name: '', buyer2Name: '',
  buyerVesting: 'Joint Tenants',
  agentName: '', agentBrokerage: '',
  brokerRelationship: 'Transaction-Broker',
  seller1Name: '', seller2Name: '',
  listingAgent: '', listingBrokerage: '',
  // Step 3 — Price & EMD
  purchasePrice: '', emdAmount: '',
  emdForm: 'Wire Transfer', emdHolder: '',
  sellerConcession: '', cashAtClosing: '',
  buyerFundsAvailable: 'Yes',
  compSellerPct: '', compSellerAmt: '',
  compBuyerPct: '', compBuyerAmt: '',
  // Step 4 — Financing
  loanType: 'Conventional', newLoanAmount: '',
  buyerOccupancy: 'Yes',
  // Step 5 — Deadlines
  closingDate: '', possessionDate: '', possessionTime: '',
  acceptanceDeadlineDate: '', acceptanceDeadlineTime: '',
  altEmdDeadline: '',
  inspTermDeadline: '', inspObjDeadline: '',
  inspResDeadline: '', propInsTermDeadline: '',
  loanAppDeadline: '', loanObjDeadline: '', loanAvailDeadline: '',
  appraisalDeadline: '', appraisalObjDeadline: '', appraisalResDeadline: '',
  recordTitleDeadline: '', recordTitleObjDeadline: '', titleResDeadline: '',
  sellerPropDiscDeadline: '', assocDocDeadline: '',
  extendHolidays: 'Yes',
  // Step 6 — Inclusions
  otherInclusions: '', exclusions: '', leasedItems: '',
  buyerAssumesLease: 'No',
  parkingFacilities: '', storageFacilities: '', garageDoorRemotes: '',
  // Step 7 — Water Rights
  waterSource: 'Municipal / Public', wellPermitNumber: '',
  deedWaterRights: 'No', waterRightsDesc: '',
  waterStockCerts: 'No', waterTransferFees: '',
  waterTransferFeesPaidBy: 'N/A', waterRightsExamDeadline: '',
  // Step 8 — HOA & Title
  hoaApplicable: 'No', hoaName: '', hoaMonthlyDues: '',
  specialAssessmentPaidBy: 'N/A', assocDocTermDeadline: '',
  titleInsSelectedBy: 'Seller selects (Seller pays)',
  closingCompany: '',
  ownerExtCoverage: 'Yes',
  oecPaidBy: 'Seller',
  deedType: 'Special Warranty Deed (CO default)',
  closingServicesFeeBy: 'Split 50/50',
  mineralRightsExamDeadline: '',
  // Step 9 — Disclosures
  leadPaint: 'Not applicable (post-1978)', leadPaintTermDeadline: '',
  methHistory: 'Seller has no knowledge',
  radonDisclosure: 'Yes',
  coAlarms: 'Yes',
  mineralRightsSeparate: 'No',
  oilGasActivity: 'Yes',
  // Step 10 — Addenda
  conditionalOnSale: 'No', buyerPropertyAddress: '',
  conditionalSaleDeadline: '',
  newIlcSurvey: 'No', ilcOrderedBy: 'N/A', ilcDeadline: '',
  postClosingOccupancy: 'No',
  crecClosingInstructions: 'Yes',
  additionalProvisions: '',
  // Step 11 — Review
  agentConfirmName: '', agentConfirmDate: '',
};

// ─── Helper components ────────────────────────────────────────────────────────

function CoBadge() {
  return <span className="cbs-co-badge">CO</span>;
}

function Chips({ options, value, onChange }) {
  return (
    <div className="rpa-chip-group">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          className={`rpa-chip${value === opt ? ' active' : ''}`}
          onClick={() => onChange(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function Field({ label, hint, badge, children }) {
  return (
    <div className="rpa-field">
      {label && (
        <label className="rpa-field-label">
          {label}{badge && <CoBadge />}
        </label>
      )}
      {hint && <div className="rpa-field-hint">{hint}</div>}
      {children}
    </div>
  );
}

function SDiv({ children }) {
  return <div className="cbs-sdiv">{children}</div>;
}

function WarnBox({ children }) {
  return (
    <div className="rpa-info-box rpa-info-box--warn" style={{ marginBottom: 16 }}>
      {children}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CBSWizardModal({
  isOpen,
  onClose,
  onComplete,
  onProgressUpdate,
  transaction,
  draftData,
  participants = [],
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [draftId, setDraftId] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState({ ...CBS_INITIAL_DATA });
  const [standardInclusions, setStandardInclusions] = useState([...CBS_STANDARD_INCLUSIONS]);
  const [sellerOptInclusions, setSellerOptInclusions] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Tracks whether we have already populated formData for this open session.
  // Without this, every onProgressUpdate call re-triggers the setup effect and
  // resets the form fields to whatever draftData.steps had at open time.
  const initializedRef = useRef(false);

  // ── Auth token helper ────────────────────────────────────────────────────
  const getFirebaseToken = () => new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(false).then(resolve).catch(reject);
    } else {
      const { onAuthStateChanged } = require('firebase/auth');
      const unsub = onAuthStateChanged(auth, user => {
        unsub();
        if (user) user.getIdToken(false).then(resolve).catch(reject);
        else reject(new Error('Not authenticated'));
      });
      setTimeout(() => reject(new Error('Auth timeout')), 10000);
    }
  });

  // ── Body scroll lock ─────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // ── Pre-fill from draftData or transaction on open ───────────────────────
  useEffect(() => {
    if (!isOpen) { initializedRef.current = false; return; }
    // Run only once per open session. Without this guard, every onProgressUpdate
    // call changes draftData → re-triggers this effect → resets formData to the
    // stale draft snapshot, blanking out fields the user just filled in.
    if (initializedRef.current) return;
    initializedRef.current = true;

    const getParticipantsByRole = (...roles) => (participants || []).filter(p => {
      const r = (p.role || p.participantRole || p.type || '').toUpperCase().replace(/[\s-]+/g, '_');
      return roles.some(role => r === role);
    });
    const fullName = (p) => p ? (p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()) : '';

    let currentUser = {};
    try { currentUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch {}
    const agentFullName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
    let brokerage = currentUser.brokerageName || currentUser.company || '';

    const buyers  = getParticipantsByRole('BUYER');
    const sellers = getParticipantsByRole('SELLER');
    const listingAgentP = getParticipantsByRole('LISTING_AGENT', 'CO_LISTING_AGENT')[0];

    const txnAddress = transaction?.propertyAddress || transaction?.address || '';
    const txnCity    = transaction?.propertyCity    || transaction?.city    || '';
    const txnZip     = transaction?.propertyZip     || transaction?.zipCode || transaction?.zip || '';

    if (draftData) {
      // Resuming — restore from saved draft
      setDraftId(draftData.id || null);
      setCurrentStep(draftData.currentStep || 1);
      setCompletedSteps(Array.isArray(draftData.completedSteps) ? draftData.completedSteps : []);

      const s = (n) => draftData.steps?.[String(n)]?.data || {};
      const s1 = s(1); const s2 = s(2); const s3 = s(3); const s4 = s(4);
      const s5 = s(5); const s6 = s(6); const s7 = s(7); const s8 = s(8);
      const s9 = s(9); const s10 = s(10);

      // API returns buyers/sellers as arrays of {firstName, lastName} objects
      const draftBuyer1 = Array.isArray(s2.buyers) && s2.buyers[0]
        ? `${s2.buyers[0].firstName || ''} ${s2.buyers[0].lastName || ''}`.trim()
        : s2.buyer1Name || '';
      const draftBuyer2 = Array.isArray(s2.buyers) && s2.buyers[1]
        ? `${s2.buyers[1].firstName || ''} ${s2.buyers[1].lastName || ''}`.trim()
        : s2.buyer2Name || '';
      const draftSeller1 = Array.isArray(s2.sellers) && s2.sellers[0]
        ? `${s2.sellers[0].firstName || ''} ${s2.sellers[0].lastName || ''}`.trim()
        : s2.seller1Name || '';
      const draftSeller2 = Array.isArray(s2.sellers) && s2.sellers[1]
        ? `${s2.sellers[1].firstName || ''} ${s2.sellers[1].lastName || ''}`.trim()
        : s2.seller2Name || '';

      // Normalize broker relationship from backend enum to display value
      const normBrokerRel = (val) => {
        if (!val) return 'Transaction-Broker';
        const v = val.toUpperCase().replace(/[\s-]+/g, '_');
        if (v === 'TRANSACTION_BROKER') return 'Transaction-Broker';
        if (v === 'BUYERS_AGENT' || v === 'BUYER_AGENT') return "Buyer's Agent";
        if (v === 'CUSTOMER') return 'Customer';
        return val;
      };

      setFormData({
        ...CBS_INITIAL_DATA,
        // Step 1
        propertyAddress: s1.address   || txnAddress,
        city:            s1.city      || txnCity,
        state:           s1.state     || 'CO',
        zip:             s1.zip       || txnZip,
        county:          s1.county    || '',
        legalDescription: s1.legalDescription || '',
        propertyType:    s1.propertyType || 'SFR',
        mlsNumber:       s1.mlsNumber || '',
        listPrice:       s1.listPrice   || transaction?.listPrice || transaction?.price || '',
        // Step 2 — API uses arrays for buyers/sellers and buyerAgentName/buyerBrokerage keys
        buyer1Name:      draftBuyer1 || fullName(buyers[0]) || transaction?.buyerName || '',
        buyer2Name:      draftBuyer2 || fullName(buyers[1]) || '',
        buyerVesting:    s2.buyerVesting || 'Joint Tenants',
        agentName:       s2.buyerAgentName || s2.agentName || agentFullName,
        agentBrokerage:  s2.buyerBrokerage || s2.agentBrokerage || brokerage,
        brokerRelationship: normBrokerRel(s2.brokerRelationship),
        seller1Name:     draftSeller1 || fullName(sellers[0]) || transaction?.sellerName || '',
        seller2Name:     draftSeller2 || fullName(sellers[1]) || '',
        listingAgent:    s2.listingAgent || s2.listingAgentName || fullName(listingAgentP) || '',
        listingBrokerage: s2.listingBrokerage || s2.listingAgentBrokerage || listingAgentP?.company || '',
        // Step 3
        purchasePrice: s3.purchasePrice || '', emdAmount: s3.emdAmount || '',
        emdForm: s3.emdForm || 'Wire Transfer', emdHolder: s3.emdHolder || '',
        sellerConcession: s3.sellerConcession || '', cashAtClosing: s3.cashAtClosing || '',
        buyerFundsAvailable: s3.buyerFundsAvailable || 'Yes',
        compSellerPct: s3.compSellerPct || '', compSellerAmt: s3.compSellerAmt || '',
        compBuyerPct: s3.compBuyerPct || '', compBuyerAmt: s3.compBuyerAmt || '',
        // Step 4
        loanType: s4.loanType || 'Conventional',
        newLoanAmount: s4.newLoanAmount || '',
        buyerOccupancy: s4.buyerOccupancy || 'Yes',
        // Step 5
        closingDate: s5.closingDate || '', possessionDate: s5.possessionDate || '',
        possessionTime: s5.possessionTime || '',
        acceptanceDeadlineDate: s5.acceptanceDeadlineDate || '',
        acceptanceDeadlineTime: s5.acceptanceDeadlineTime || '',
        altEmdDeadline: s5.altEmdDeadline || '',
        inspTermDeadline: s5.inspTermDeadline || '', inspObjDeadline: s5.inspObjDeadline || '',
        inspResDeadline: s5.inspResDeadline || '', propInsTermDeadline: s5.propInsTermDeadline || '',
        loanAppDeadline: s5.loanAppDeadline || '', loanObjDeadline: s5.loanObjDeadline || '',
        loanAvailDeadline: s5.loanAvailDeadline || '',
        appraisalDeadline: s5.appraisalDeadline || '', appraisalObjDeadline: s5.appraisalObjDeadline || '',
        appraisalResDeadline: s5.appraisalResDeadline || '',
        recordTitleDeadline: s5.recordTitleDeadline || '', recordTitleObjDeadline: s5.recordTitleObjDeadline || '',
        titleResDeadline: s5.titleResDeadline || '',
        sellerPropDiscDeadline: s5.sellerPropDiscDeadline || '',
        assocDocDeadline: s5.assocDocDeadline || '',
        extendHolidays: s5.extendHolidays || 'Yes',
        // Step 6
        otherInclusions: s6.otherInclusions || '', exclusions: s6.exclusions || '',
        leasedItems: s6.leasedItems || '', buyerAssumesLease: s6.buyerAssumesLease || 'No',
        parkingFacilities: s6.parkingFacilities || '',
        storageFacilities: s6.storageFacilities || '',
        garageDoorRemotes: s6.garageDoorRemotes || '',
        // Step 7
        waterSource: s7.waterSource || 'Municipal / Public', wellPermitNumber: s7.wellPermitNumber || '',
        deedWaterRights: s7.deedWaterRights || 'No', waterRightsDesc: s7.waterRightsDesc || '',
        waterStockCerts: s7.waterStockCerts || 'No', waterTransferFees: s7.waterTransferFees || '',
        waterTransferFeesPaidBy: s7.waterTransferFeesPaidBy || 'N/A',
        waterRightsExamDeadline: s7.waterRightsExamDeadline || '',
        // Step 8
        hoaApplicable: s8.hoaApplicable || 'No', hoaName: s8.hoaName || '',
        hoaMonthlyDues: s8.hoaMonthlyDues || '',
        specialAssessmentPaidBy: s8.specialAssessmentPaidBy || 'N/A',
        assocDocTermDeadline: s8.assocDocTermDeadline || '',
        titleInsSelectedBy: s8.titleInsSelectedBy || 'Seller selects (Seller pays)',
        closingCompany: s8.closingCompany || '',
        ownerExtCoverage: s8.ownerExtCoverage || 'Yes',
        oecPaidBy: s8.oecPaidBy || 'Seller',
        deedType: s8.deedType || 'Special Warranty Deed (CO default)',
        closingServicesFeeBy: s8.closingServicesFeeBy || 'Split 50/50',
        mineralRightsExamDeadline: s8.mineralRightsExamDeadline || '',
        // Step 9
        leadPaint: s9.leadPaint || 'Not applicable (post-1978)',
        leadPaintTermDeadline: s9.leadPaintTermDeadline || '',
        methHistory: s9.methHistory || 'Seller has no knowledge',
        radonDisclosure: s9.radonDisclosure || 'Yes',
        coAlarms: s9.coAlarms || 'Yes',
        mineralRightsSeparate: s9.mineralRightsSeparate || 'No',
        oilGasActivity: s9.oilGasActivity || 'Yes',
        // Step 10
        conditionalOnSale: s10.conditionalOnSale || 'No',
        buyerPropertyAddress: s10.buyerPropertyAddress || '',
        conditionalSaleDeadline: s10.conditionalSaleDeadline || '',
        newIlcSurvey: s10.newIlcSurvey || 'No', ilcOrderedBy: s10.ilcOrderedBy || 'N/A',
        ilcDeadline: s10.ilcDeadline || '',
        postClosingOccupancy: s10.postClosingOccupancy || 'No',
        crecClosingInstructions: s10.crecClosingInstructions || 'Yes',
        additionalProvisions: s10.additionalProvisions || '',
        agentConfirmName: agentFullName,
      });

      if (Array.isArray(s6.standardInclusions)) setStandardInclusions(s6.standardInclusions);
      if (Array.isArray(s6.sellerOptInclusions)) setSellerOptInclusions(s6.sellerOptInclusions);
    } else {
      // Fresh start — pre-fill from transaction and participants
      setDraftId(null);
      setCurrentStep(1);
      setCompletedSteps([]);
      setStandardInclusions([...CBS_STANDARD_INCLUSIONS]);
      setSellerOptInclusions([]);
      setFormData({
        ...CBS_INITIAL_DATA,
        propertyAddress: txnAddress,
        city: txnCity,
        zip: txnZip,
        mlsNumber: transaction?.mlsNumber || '',
        listPrice: transaction?.listPrice || transaction?.price || '',
        buyer1Name: fullName(buyers[0]) || transaction?.buyerName || '',
        buyer2Name: fullName(buyers[1]) || '',
        agentName: agentFullName,
        agentBrokerage: brokerage,
        seller1Name: fullName(sellers[0]) || transaction?.sellerName || '',
        seller2Name: fullName(sellers[1]) || '',
        listingAgent: fullName(listingAgentP) || '',
        listingBrokerage: listingAgentP?.company || '',
        agentConfirmName: agentFullName,
      });
    }
    setIsSubmitted(false);
    setSaveError(null);
  }, [isOpen, draftData, transaction, participants]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Form helpers ─────────────────────────────────────────────────────────
  const setField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const inp = (key, extra = {}) => (
    <input
      className="rpa-input"
      value={formData[key]}
      onChange={e => setField(key, e.target.value)}
      {...extra}
    />
  );
  const dateInp = (key) => (
    <input
      className="rpa-input"
      type="date"
      value={formData[key]}
      onChange={e => setField(key, e.target.value)}
    />
  );
  const textArea = (key, rows = 2, placeholder = '') => (
    <textarea
      className="rpa-input rpa-textarea"
      rows={rows}
      placeholder={placeholder}
      value={formData[key]}
      onChange={e => setField(key, e.target.value)}
    />
  );
  const chips = (key, options) => (
    <Chips options={options} value={formData[key]} onChange={v => setField(key, v)} />
  );
  const readOnly = (value) => (
    <input className="rpa-input" value={value} readOnly style={{ background: '#f5f5f5', color: '#888' }} />
  );

  // ── Step payload builders ────────────────────────────────────────────────
  const getCBSStepPayload = (step) => {
    switch (step) {
      case 1: return {
        address: formData.propertyAddress, city: formData.city, state: 'CO',
        zip: formData.zip, county: formData.county,
        legalDescription: formData.legalDescription, propertyType: formData.propertyType,
        mlsNumber: formData.mlsNumber, listPrice: formData.listPrice,
      };
      case 2: return {
        buyer1Name: formData.buyer1Name, buyer2Name: formData.buyer2Name,
        buyerVesting: formData.buyerVesting, agentName: formData.agentName,
        agentBrokerage: formData.agentBrokerage, brokerRelationship: formData.brokerRelationship,
        seller1Name: formData.seller1Name, seller2Name: formData.seller2Name,
        listingAgent: formData.listingAgent, listingBrokerage: formData.listingBrokerage,
      };
      case 3: return {
        purchasePrice: formData.purchasePrice, emdAmount: formData.emdAmount,
        emdForm: formData.emdForm, emdHolder: formData.emdHolder,
        sellerConcession: formData.sellerConcession, cashAtClosing: formData.cashAtClosing,
        buyerFundsAvailable: formData.buyerFundsAvailable,
        compSellerPct: formData.compSellerPct, compSellerAmt: formData.compSellerAmt,
        compBuyerPct: formData.compBuyerPct, compBuyerAmt: formData.compBuyerAmt,
      };
      case 4: return {
        loanType: formData.loanType, newLoanAmount: formData.newLoanAmount,
        buyerOccupancy: formData.buyerOccupancy,
      };
      case 5: return {
        closingDate: formData.closingDate, possessionDate: formData.possessionDate,
        possessionTime: formData.possessionTime,
        acceptanceDeadlineDate: formData.acceptanceDeadlineDate,
        acceptanceDeadlineTime: formData.acceptanceDeadlineTime,
        altEmdDeadline: formData.altEmdDeadline,
        inspTermDeadline: formData.inspTermDeadline, inspObjDeadline: formData.inspObjDeadline,
        inspResDeadline: formData.inspResDeadline, propInsTermDeadline: formData.propInsTermDeadline,
        loanAppDeadline: formData.loanAppDeadline, loanObjDeadline: formData.loanObjDeadline,
        loanAvailDeadline: formData.loanAvailDeadline,
        appraisalDeadline: formData.appraisalDeadline, appraisalObjDeadline: formData.appraisalObjDeadline,
        appraisalResDeadline: formData.appraisalResDeadline,
        recordTitleDeadline: formData.recordTitleDeadline, recordTitleObjDeadline: formData.recordTitleObjDeadline,
        titleResDeadline: formData.titleResDeadline,
        sellerPropDiscDeadline: formData.sellerPropDiscDeadline,
        assocDocDeadline: formData.assocDocDeadline, extendHolidays: formData.extendHolidays,
      };
      case 6: return {
        standardInclusions, sellerOptInclusions,
        otherInclusions: formData.otherInclusions, exclusions: formData.exclusions,
        leasedItems: formData.leasedItems, buyerAssumesLease: formData.buyerAssumesLease,
        parkingFacilities: formData.parkingFacilities, storageFacilities: formData.storageFacilities,
        garageDoorRemotes: formData.garageDoorRemotes,
      };
      case 7: return {
        waterSource: formData.waterSource, wellPermitNumber: formData.wellPermitNumber,
        deedWaterRights: formData.deedWaterRights, waterRightsDesc: formData.waterRightsDesc,
        waterStockCerts: formData.waterStockCerts, waterTransferFees: formData.waterTransferFees,
        waterTransferFeesPaidBy: formData.waterTransferFeesPaidBy,
        waterRightsExamDeadline: formData.waterRightsExamDeadline,
      };
      case 8: return {
        hoaApplicable: formData.hoaApplicable, hoaName: formData.hoaName,
        hoaMonthlyDues: formData.hoaMonthlyDues,
        specialAssessmentPaidBy: formData.specialAssessmentPaidBy,
        assocDocTermDeadline: formData.assocDocTermDeadline,
        titleInsSelectedBy: formData.titleInsSelectedBy, closingCompany: formData.closingCompany,
        ownerExtCoverage: formData.ownerExtCoverage, oecPaidBy: formData.oecPaidBy,
        deedType: formData.deedType, closingServicesFeeBy: formData.closingServicesFeeBy,
        mineralRightsExamDeadline: formData.mineralRightsExamDeadline,
      };
      case 9: return {
        leadPaint: formData.leadPaint, leadPaintTermDeadline: formData.leadPaintTermDeadline,
        methHistory: formData.methHistory, radonDisclosure: formData.radonDisclosure,
        coAlarms: formData.coAlarms, mineralRightsSeparate: formData.mineralRightsSeparate,
        oilGasActivity: formData.oilGasActivity,
      };
      case 10: return {
        conditionalOnSale: formData.conditionalOnSale,
        buyerPropertyAddress: formData.buyerPropertyAddress,
        conditionalSaleDeadline: formData.conditionalSaleDeadline,
        newIlcSurvey: formData.newIlcSurvey, ilcOrderedBy: formData.ilcOrderedBy,
        ilcDeadline: formData.ilcDeadline, postClosingOccupancy: formData.postClosingOccupancy,
        crecClosingInstructions: formData.crecClosingInstructions,
        additionalProvisions: formData.additionalProvisions,
      };
      case 11: return { agentConfirmName: formData.agentConfirmName, agentConfirmDate: formData.agentConfirmDate };
      default: return {};
    }
  };

  // ── Save and navigate ────────────────────────────────────────────────────
  const handleNext = async () => {
    if (isSaving) return;
    setSaveError(null);
    const txnId = transaction?.id;
    if (!txnId || !draftId) { goToStep(currentStep + 1); return; }

    setIsSaving(true);
    try {
      const token = await getFirebaseToken();
      const payload = getCBSStepPayload(currentStep);
      await apiClient.put(
        API_ENDPOINTS.cbsWizardSaveStep(txnId, draftId, currentStep, true),
        { data: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const next = currentStep + 1;
      const newCompleted = completedSteps.includes(currentStep)
        ? completedSteps
        : [...completedSteps, currentStep];
      setCompletedSteps(newCompleted);
      onProgressUpdate && onProgressUpdate(newCompleted, next);
      goToStep(next);
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || 'Save failed. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => { if (currentStep > 1) goToStep(currentStep - 1); };

  const goToStep = (n) => { setCurrentStep(Math.max(1, Math.min(CBS_TOTAL_STEPS, n))); };

  const handleSubmit = async () => {
    if (isSaving) return;
    setSaveError(null);
    const txnId = transaction?.id;
    setIsSaving(true);
    try {
      const token = await getFirebaseToken();
      if (txnId && draftId) {
        await apiClient.put(
          API_ENDPOINTS.cbsWizardSaveStep(txnId, draftId, 11, true),
          { data: getCBSStepPayload(11) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await apiClient.post(
          API_ENDPOINTS.cbsWizardSubmit(txnId, draftId),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      const allCompleted = Array.from({ length: CBS_TOTAL_STEPS }, (_, i) => i + 1);
      setCompletedSteps(allCompleted);
      onProgressUpdate && onProgressUpdate(allCompleted, CBS_TOTAL_STEPS);
      setIsSubmitted(true);
      onComplete && onComplete();
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || 'Submission failed. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Toggle inclusion helper ──────────────────────────────────────────────
  const toggleInclusion = (list, setList, item) => {
    setList(prev => prev.includes(item) ? prev.filter(x => x !== item) : [...prev, item]);
  };

  // ── Step renderers ───────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="rpa-card">
      <div className="rpa-section-title">Property Details</div>
      <div className="rpa-field-row">
        <Field label="Property Address"><input className="rpa-input" value={formData.propertyAddress} readOnly style={{ background: '#f5f5f5', color: '#888' }} /></Field>
        <Field label="City / State / Zip">
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="rpa-input" value={formData.city} onChange={e => setField('city', e.target.value)} placeholder="City" style={{ flex: 2 }} />
            <input className="rpa-input" value="CO" readOnly style={{ flex: 0.8, background: '#f5f5f5', color: '#888' }} />
            <input className="rpa-input" value={formData.zip} onChange={e => setField('zip', e.target.value)} placeholder="Zip" style={{ flex: 1 }} />
          </div>
        </Field>
      </div>
      <div className="rpa-field-row">
        <Field label="County" badge>{inp('county', { placeholder: 'e.g. El Paso County' })}</Field>
        <Field label="MLS #" hint="Listing reference — not a field on the CBS contract.">{inp('mlsNumber', { placeholder: 'MLS number' })}</Field>
      </div>
      <Field label="Legal Description" badge hint="Enter legal description as it appears on title.">
        {textArea('legalDescription', 2, 'Legal description...')}
      </Field>
      <div className="rpa-field-row">
        <Field label="Property Type">
          {chips('propertyType', ['SFR', 'Condo', 'Townhome', 'Multi-family', 'Other'])}
        </Field>
        <Field label="List Price" hint="Listing/MLS reference — not on the CBS. The contract uses Purchase Price (Step 3).">{inp('listPrice', { placeholder: '$' })}</Field>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <>
      <div className="rpa-card">
        <div className="rpa-section-title">Buyer(s)</div>
        <div className="rpa-field-row">
          <Field label="Buyer 1 Full Name">
            <input className="rpa-input" value={formData.buyer1Name}
              onChange={e => setField('buyer1Name', e.target.value)}
              placeholder="Full legal name" />
          </Field>
          <Field label="Buyer 2 Full Name">
            <input className="rpa-input" value={formData.buyer2Name}
              onChange={e => setField('buyer2Name', e.target.value)}
              placeholder="Full legal name (if applicable)" />
          </Field>
        </div>
        <Field label="Buyer Title Vesting" badge>
          {chips('buyerVesting', ['Joint Tenants', 'Tenants in Common', 'Other'])}
        </Field>
        <div className="rpa-field-row">
          <Field label="Buyer Agent">{readOnly(formData.agentName)}</Field>
          <Field label="Buyer Brokerage">{readOnly(formData.agentBrokerage)}</Field>
        </div>
        <Field label="Broker Relationship" badge>
          {chips('brokerRelationship', ["Buyer's Agent", 'Transaction-Broker', 'Customer'])}
        </Field>
      </div>
      <div className="rpa-card">
        <div className="rpa-section-title">Seller(s)</div>
        <div className="rpa-field-row">
          <Field label="Seller 1 Full Name">
            <input className="rpa-input" value={formData.seller1Name}
              readOnly={!!formData.seller1Name}
              style={formData.seller1Name ? { background: '#f5f5f5', color: '#888' } : undefined}
              onChange={formData.seller1Name ? undefined : e => setField('seller1Name', e.target.value)}
              placeholder={formData.seller1Name ? undefined : 'Full legal name'} />
          </Field>
          <Field label="Seller 2 Full Name">
            <input className="rpa-input" value={formData.seller2Name}
              readOnly={!!formData.seller2Name}
              style={formData.seller2Name ? { background: '#f5f5f5', color: '#888' } : undefined}
              onChange={formData.seller2Name ? undefined : e => setField('seller2Name', e.target.value)}
              placeholder={formData.seller2Name ? undefined : 'Full legal name (if applicable)'} />
          </Field>
        </div>
        <div className="rpa-field-row">
          <Field label="Listing Agent">{readOnly(formData.listingAgent)}</Field>
          <Field label="Listing Brokerage">{readOnly(formData.listingBrokerage)}</Field>
        </div>
      </div>
    </>
  );

  const renderStep3 = () => (
    <div className="rpa-card">
      <div className="rpa-section-title">Price & Earnest Money</div>
      <div className="rpa-field-row">
        <Field label="Purchase Price">{inp('purchasePrice', { placeholder: '$' })}</Field>
        <Field label="Earnest Money Amount">{inp('emdAmount', { placeholder: '$', onChange: e => setField('emdAmount', e.target.value.replace(/[^0-9.]/g, '')) })}</Field>
      </div>
      <Field label="EMD Form" badge>
        {chips('emdForm', ['Wire Transfer', 'Certified Check', 'Personal Check', 'Other'])}
      </Field>
      <Field label="Earnest Money Holder" badge>
        {inp('emdHolder', { placeholder: 'Title company name (e.g. First American Title)' })}
      </Field>
      <div className="rpa-field-row">
        <Field label="Seller Concession" badge>{inp('sellerConcession', { placeholder: '$ amount (optional)' })}</Field>
        <Field label="Cash at Closing">{inp('cashAtClosing', { placeholder: '$' })}</Field>
      </div>
      <Field label="Buyer has available funds?">
        {chips('buyerFundsAvailable', ['Yes', 'No'])}
      </Field>
      <div className="rpa-section-title" style={{ marginTop: 20 }}>Buyer's Brokerage Compensation (§29)</div>
      <div className="rpa-field-row">
        <Field label="Compensation paid by Seller">
          <div style={{ display: 'flex', gap: 8 }}>
            {inp('compSellerPct', { placeholder: '%', style: { maxWidth: 80 } })}
            {inp('compSellerAmt', { placeholder: 'Or $ amount' })}
          </div>
        </Field>
        <Field label="Compensation paid by Buyer">
          <div style={{ display: 'flex', gap: 8 }}>
            {inp('compBuyerPct', { placeholder: '%', style: { maxWidth: 80 } })}
            {inp('compBuyerAmt', { placeholder: 'Or $ amount' })}
          </div>
        </Field>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="rpa-card">
      <div className="rpa-section-title">Financing</div>
      <Field label="Loan Type">
        {chips('loanType', ['Conventional', 'FHA', 'VA', 'Bond', 'Cash (no loan)', 'Other'])}
      </Field>
      <div className="rpa-field-row">
        <Field label="New Loan Amount">{inp('newLoanAmount', { placeholder: '$' })}</Field>
        <Field label="Buyer will occupy as principal residence?">
          {chips('buyerOccupancy', ['Yes', 'No — investment / non-owner occupied'])}
        </Field>
      </div>
      <WarnBox>
        Per CBS §4.7, Seller or Private Financing documents should be prepared by a licensed Colorado attorney or mortgage loan originator — not covered in this wizard.
      </WarnBox>
    </div>
  );

  const renderStep5 = () => (
    <div className="rpa-card">
      <div className="rpa-section-title">
        Dates & Deadlines <span className="cbs-co-badge">CO — all set upfront in CBS contract</span>
      </div>
      <WarnBox>
        CBS requires all deadlines to be set at time of offer. These replace contingency periods — deadlines are strict (Time is of the essence).
      </WarnBox>

      <SDiv>Closing & Possession</SDiv>
      <div className="rpa-field-row">
        <Field label="Closing Date">{dateInp('closingDate')}</Field>
        <Field label="Possession Date">{dateInp('possessionDate')}</Field>
      </div>
      <div className="rpa-field-row">
        <Field label="Possession Time" badge>
          <input className="rpa-input" type="time" value={formData.possessionTime} onChange={e => setField('possessionTime', e.target.value)} />
        </Field>
        <Field label="Acceptance Deadline Date" badge>{dateInp('acceptanceDeadlineDate')}</Field>
      </div>
      <div className="rpa-field-row">
        <Field label="Acceptance Deadline Time" badge>
          <input className="rpa-input" type="time" value={formData.acceptanceDeadlineTime} onChange={e => setField('acceptanceDeadlineTime', e.target.value)} />
        </Field>
        <div className="rpa-field" />
      </div>

      <SDiv>Earnest Money</SDiv>
      <Field label="Alternative EMD Deadline" badge hint="Leave blank if EMD is due at time of offer.">
        {dateInp('altEmdDeadline')}
      </Field>

      <SDiv>Inspection & Due Diligence <span className="cbs-co-badge">Replaces CA inspection contingency</span></SDiv>
      <div className="rpa-field-row">
        <Field label="Inspection Termination Deadline" badge>{dateInp('inspTermDeadline')}</Field>
        <Field label="Inspection Objection Deadline" badge>{dateInp('inspObjDeadline')}</Field>
      </div>
      <div className="rpa-field-row">
        <Field label="Inspection Resolution Deadline" badge>{dateInp('inspResDeadline')}</Field>
        <Field label="Property Insurance Termination Deadline" badge>{dateInp('propInsTermDeadline')}</Field>
      </div>

      <SDiv>Loan <span className="cbs-co-badge">Replaces CA loan contingency</span></SDiv>
      <div className="rpa-field-row">
        <Field label="New Loan Application Deadline">{dateInp('loanAppDeadline')}</Field>
        <Field label="Loan Objection Deadline" badge>{dateInp('loanObjDeadline')}</Field>
      </div>
      <Field label="New Loan Availability Deadline" badge>{dateInp('loanAvailDeadline')}</Field>

      <SDiv>Appraisal <span className="cbs-co-badge">Replaces CA appraisal contingency</span></SDiv>
      <div className="rpa-field-row">
        <Field label="Appraisal Deadline" badge>{dateInp('appraisalDeadline')}</Field>
        <Field label="Appraisal Objection Deadline" badge>{dateInp('appraisalObjDeadline')}</Field>
      </div>
      <Field label="Appraisal Resolution Deadline" badge>{dateInp('appraisalResDeadline')}</Field>

      <SDiv>Title & Seller Disclosures</SDiv>
      <div className="rpa-field-row">
        <Field label="Record Title Deadline" badge>{dateInp('recordTitleDeadline')}</Field>
        <Field label="Record Title Objection Deadline" badge>{dateInp('recordTitleObjDeadline')}</Field>
      </div>
      <div className="rpa-field-row">
        <Field label="Title Resolution Deadline" badge>{dateInp('titleResDeadline')}</Field>
        <Field label="Seller's Property Disclosure Deadline" badge>{dateInp('sellerPropDiscDeadline')}</Field>
      </div>
      <div className="rpa-field-row">
        <Field label="Association Documents Deadline" badge hint="If HOA applicable.">
          {dateInp('assocDocDeadline')}
        </Field>
        <Field label="Extend Deadlines on Federal Holidays?" badge>
          {chips('extendHolidays', ['Yes', 'No'])}
        </Field>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="rpa-card">
      <div className="rpa-section-title">
        Inclusions & Exclusions <span className="cbs-co-badge">CO Specific</span>
      </div>
      <WarnBox>
        In Colorado CBS, inclusions and exclusions are set in the contract. The following are automatically included unless excluded below.
      </WarnBox>

      <SDiv>Standard Attached Inclusions (auto-included unless excluded)</SDiv>
      <div className="cbs-checkbox-grid">
        {CBS_STANDARD_INCLUSIONS.map(item => (
          <label key={item} className={`cbs-checkbox-item${standardInclusions.includes(item) ? ' checked' : ''}`}>
            <input
              type="checkbox"
              checked={standardInclusions.includes(item)}
              onChange={() => toggleInclusion(standardInclusions, setStandardInclusions, item)}
            />
            {item}
          </label>
        ))}
      </div>

      <SDiv>Seller-owned items to include (check if applicable)</SDiv>
      <div className="cbs-checkbox-grid">
        {CBS_SELLER_OPT_INCLUSIONS.map(item => (
          <label key={item} className={`cbs-checkbox-item${sellerOptInclusions.includes(item) ? ' checked' : ''}`}>
            <input
              type="checkbox"
              checked={sellerOptInclusions.includes(item)}
              onChange={() => toggleInclusion(sellerOptInclusions, setSellerOptInclusions, item)}
            />
            {item}
          </label>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <Field label="Other Inclusions" badge>
          {textArea('otherInclusions', 2, 'List any additional items included in the purchase price...')}
        </Field>
        <Field label="Exclusions" badge>
          {textArea('exclusions', 2, 'List any items excluded from the sale that might otherwise be assumed included...')}
        </Field>
        <Field label="Leased Items" badge hint="e.g. Solar panels (lease), water softener (Culligan lease)">
          {textArea('leasedItems', 2, 'List leased items...')}
        </Field>
        <Field label="Buyer assumes leased items?">
          {chips('buyerAssumesLease', ['Yes — buyer assumes obligations', 'No — seller to resolve before closing'])}
        </Field>
        <div className="rpa-field-row">
          <Field label="Parking Facilities" badge>
            {inp('parkingFacilities', { placeholder: 'Describe parking rights / facilities included' })}
          </Field>
          <Field label="Storage Facilities" badge>
            {inp('storageFacilities', { placeholder: 'Describe storage rights / facilities included' })}
          </Field>
        </div>
        <Field label="Garage Door Remotes">
          {inp('garageDoorRemotes', { placeholder: '# of remotes', style: { maxWidth: 140 } })}
        </Field>
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="rpa-card">
      <div className="rpa-section-title">
        Water Rights <span className="cbs-co-badge">CO Specific — no CA equivalent</span>
      </div>
      <WarnBox>
        Water rights are a significant legal matter in Colorado. Buyers should consult an attorney if water rights or well rights are part of this transaction.
      </WarnBox>
      <Field label="Source of Potable Water" badge>
        {chips('waterSource', ['Municipal / Public', 'Well', 'Community', 'Shared Well', 'Other'])}
      </Field>
      <div className="rpa-field-row">
        <Field label="Well Permit #" badge>
          {inp('wellPermitNumber', { placeholder: 'If well — enter permit number' })}
        </Field>
        <Field label="Deeded Water Rights?" badge>
          {chips('deedWaterRights', ['Yes — included in sale', 'No'])}
        </Field>
      </div>
      <Field label="Water Rights Description">
        {textArea('waterRightsDesc', 2, 'Legal description of water rights (if applicable)...')}
      </Field>
      <div className="rpa-field-row">
        <Field label="Water Stock Certificates?" badge>
          {chips('waterStockCerts', ['Yes — to be transferred at closing', 'No'])}
        </Field>
        <Field label="Water Transfer Fees" badge>
          {inp('waterTransferFees', { placeholder: '$ amount (if applicable)' })}
        </Field>
      </div>
      <div className="rpa-field-row">
        <Field label="Water Transfer Fees Paid By">
          {chips('waterTransferFeesPaidBy', ['Buyer', 'Seller', 'Split 50/50', 'N/A'])}
        </Field>
        <Field label="Water Rights Examination Deadline" badge>
          {dateInp('waterRightsExamDeadline')}
        </Field>
      </div>
    </div>
  );

  const renderStep8 = () => (
    <>
      <div className="rpa-card">
        <div className="rpa-section-title">HOA / Owners' Association</div>
        <Field label="HOA Applicable?">
          {chips('hoaApplicable', ['Yes', 'No'])}
        </Field>
        <div className="rpa-field-row">
          <Field label="HOA Name">{inp('hoaName', { placeholder: 'Association name' })}</Field>
          <Field label="HOA Monthly Dues">{inp('hoaMonthlyDues', { placeholder: '$' })}</Field>
        </div>
        <div className="rpa-field-row">
          <Field label="Special Assessment — Paid By" badge>
            {chips('specialAssessmentPaidBy', ['Buyer', 'Seller', 'N/A'])}
          </Field>
          <Field label="Assoc. Documents Termination Deadline" badge>
            {dateInp('assocDocTermDeadline')}
          </Field>
        </div>
      </div>
      <div className="rpa-card">
        <div className="rpa-section-title">Title Insurance <span className="cbs-co-badge">CO Specific</span></div>
        <Field label="Title Insurance Company — Selected By" badge>
          {chips('titleInsSelectedBy', ['Seller selects (Seller pays)', 'Buyer selects (Buyer pays)'])}
        </Field>
        <Field label="Closing Company (Title Co.)" badge>
          {inp('closingCompany', { placeholder: 'Title company name (CO uses title company, not escrow)' })}
        </Field>
        <div className="rpa-field-row">
          <Field label="Owner's Extended Coverage (OEC)?" badge>
            {chips('ownerExtCoverage', ['Yes — required', 'No'])}
          </Field>
          <Field label="OEC Premium Paid By">
            {chips('oecPaidBy', ['Buyer', 'Seller', 'Split 50/50', 'N/A'])}
          </Field>
        </div>
        <div className="rpa-field-row">
          <Field label="Deed Type" badge>
            <select
              className="rpa-input"
              value={formData.deedType}
              onChange={e => setField('deedType', e.target.value)}
            >
              <option>Special Warranty Deed (CO default)</option>
              <option>General Warranty Deed</option>
              <option>Bargain and Sale Deed</option>
              <option>Quit Claim Deed</option>
              <option>Personal Representative's Deed</option>
            </select>
          </Field>
          <Field label="Closing Services Fee — Paid By" badge>
            {chips('closingServicesFeeBy', ['Buyer', 'Seller', 'Split 50/50'])}
          </Field>
        </div>
        <Field label="Mineral Rights Examination Deadline" badge>
          {dateInp('mineralRightsExamDeadline')}
        </Field>
      </div>
    </>
  );

  const renderStep9 = () => (
    <>
      <div className="rpa-card">
        <div className="rpa-section-title">Federal — Required Both States</div>
        <Field label="Lead-Based Paint Disclosure">
          {chips('leadPaint', ['Required (pre-1978 construction)', 'Not applicable (post-1978)'])}
        </Field>
        <Field label="Lead-Based Paint Termination Deadline">
          {dateInp('leadPaintTermDeadline')}
        </Field>
      </div>
      <div className="rpa-card">
        <div className="rpa-section-title">Colorado Specific Disclosures <span className="cbs-co-badge">CO Only</span></div>
        <Field label="Methamphetamine History" badge>
          {chips('methHistory', ['Seller has no knowledge', 'Yes — disclose details'])}
        </Field>
        <Field label="Radon Disclosure Acknowledged" badge>
          {chips('radonDisclosure', ['Yes', 'No'])}
        </Field>
        <Field label="Carbon Monoxide Alarms — Seller Confirms" badge>
          {chips('coAlarms', ['Yes — CO alarms installed within 15 ft of each bedroom', 'No — to be installed before closing'])}
        </Field>
        <Field label="Mineral Rights — Separate from Surface?" badge>
          {chips('mineralRightsSeparate', ['Yes — sold separately', 'No — included with surface estate', 'Unknown'])}
        </Field>
        <Field label="Oil & Gas Activity Near Property" badge>
          {chips('oilGasActivity', ['Yes — buyer acknowledges CO oil & gas disclosure', 'No'])}
        </Field>
      </div>
    </>
  );

  const renderStep10 = () => (
    <div className="rpa-card">
      <div className="rpa-section-title">Addenda & Miscellaneous</div>
      <Field label="Conditional on Sale of Buyer's Property?" badge>
        {chips('conditionalOnSale', ['No', 'Yes — conditional on sale of buyer\'s current home'])}
      </Field>
      {formData.conditionalOnSale !== 'No' && (
        <>
          <div className="rpa-field-row">
            <Field label="Buyer's Current Property Address">
              {inp('buyerPropertyAddress', { placeholder: 'Address of buyer\'s current home to be sold' })}
            </Field>
            <Field label="Conditional Sale Deadline" badge>{dateInp('conditionalSaleDeadline')}</Field>
          </div>
        </>
      )}
      <Field label="New ILC or Survey Required?" badge>
        {chips('newIlcSurvey', ['No', 'Yes — New ILC', 'Yes — New Survey'])}
      </Field>
      {formData.newIlcSurvey !== 'No' && (
        <div className="rpa-field-row">
          <Field label="ILC / Survey Ordered By">
            {chips('ilcOrderedBy', ['Buyer', 'Seller', 'N/A'])}
          </Field>
          <Field label="New ILC / Survey Deadline" badge>{dateInp('ilcDeadline')}</Field>
        </div>
      )}
      <Field label="Post-Closing Occupancy Agreement?" badge>
        {chips('postClosingOccupancy', ['No', 'Yes — seller remains after closing'])}
      </Field>
      <Field label="CREC Closing Instructions Executed with Contract?" badge>
        {chips('crecClosingInstructions', ['Yes', 'No'])}
      </Field>
      <Field label="Additional Provisions (§30)" hint="Any additional provisions not covered above (not approved by CREC).">
        {textArea('additionalProvisions', 3, 'Enter additional provisions...')}
      </Field>
    </div>
  );

  const f = (v) => v || '—';

  const renderStep11 = () => (
    <>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 18 }}>
        Review all entered information before generating the CBS Offer Summary.
      </p>

      {/* Property */}
      <div className="cbs-rv-sec">
        <div className="cbs-rv-head">
          <span className="cbs-rv-head-icon">{STEP_ICONS[1]}</span>
          <span className="cbs-rv-head-title">Property</span>
        </div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Address</div><div className="cbs-rv-val">{f(formData.propertyAddress)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">City / State / Zip</div><div className="cbs-rv-val">{[formData.city, 'CO', formData.zip].filter(Boolean).join(', ') || '—'}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">County</div><div className="cbs-rv-val">{f(formData.county)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Property Type</div><div className="cbs-rv-val">{f(formData.propertyType)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">MLS #</div><div className="cbs-rv-val">{f(formData.mlsNumber)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">List Price</div><div className="cbs-rv-val">{formData.listPrice ? `$${Number(formData.listPrice.replace(/,/g, '')).toLocaleString()}` : '—'}</div></div>
      </div>

      {/* Parties */}
      <div className="cbs-rv-sec">
        <div className="cbs-rv-head">
          <span className="cbs-rv-head-icon">{STEP_ICONS[2]}</span>
          <span className="cbs-rv-head-title">Parties</span>
        </div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Buyer(s)</div><div className="cbs-rv-val">{[formData.buyer1Name, formData.buyer2Name].filter(Boolean).join(' & ') || '—'}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Buyer Vesting</div><div className="cbs-rv-val">{f(formData.buyerVesting)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Broker Relationship</div><div className="cbs-rv-val">{f(formData.brokerRelationship)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Seller(s)</div><div className="cbs-rv-val">{[formData.seller1Name, formData.seller2Name].filter(Boolean).join(' & ') || '—'}</div></div>
      </div>

      {/* Price & EMD */}
      <div className="cbs-rv-sec">
        <div className="cbs-rv-head">
          <span className="cbs-rv-head-icon">{STEP_ICONS[3]}</span>
          <span className="cbs-rv-head-title">Price & EMD</span>
        </div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Purchase Price</div><div className="cbs-rv-val">{formData.purchasePrice ? `$${Number(formData.purchasePrice.replace(/,/g, '')).toLocaleString()}` : '—'}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Earnest Money</div><div className="cbs-rv-val">{formData.emdAmount ? `$${Number(formData.emdAmount.replace(/,/g, '')).toLocaleString()}` : '—'}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">EMD Holder</div><div className="cbs-rv-val">{f(formData.emdHolder)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Seller Concession</div><div className="cbs-rv-val">{f(formData.sellerConcession)}</div></div>
      </div>

      {/* Key Deadlines */}
      <div className="cbs-rv-sec">
        <div className="cbs-rv-head">
          <span className="cbs-rv-head-icon">{STEP_ICONS[5]}</span>
          <span className="cbs-rv-head-title">Key Deadlines</span>
        </div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Closing Date</div><div className="cbs-rv-val">{f(formData.closingDate)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Possession Date / Time</div><div className="cbs-rv-val">{[formData.possessionDate, formData.possessionTime].filter(Boolean).join(' at ') || '—'}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Acceptance Deadline</div><div className="cbs-rv-val">{[formData.acceptanceDeadlineDate, formData.acceptanceDeadlineTime].filter(Boolean).join(' at ') || '—'}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Inspection Objection Deadline</div><div className="cbs-rv-val">{f(formData.inspObjDeadline)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Loan Objection Deadline</div><div className="cbs-rv-val">{f(formData.loanObjDeadline)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Appraisal Deadline</div><div className="cbs-rv-val">{f(formData.appraisalDeadline)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Record Title Deadline</div><div className="cbs-rv-val">{f(formData.recordTitleDeadline)}</div></div>
      </div>

      {/* Water Rights */}
      <div className="cbs-rv-sec">
        <div className="cbs-rv-head">
          <span className="cbs-rv-head-icon">{STEP_ICONS[7]}</span>
          <span className="cbs-rv-head-title">Water Rights</span>
        </div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Source of Water</div><div className="cbs-rv-val">{f(formData.waterSource)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Deeded Water Rights</div><div className="cbs-rv-val">{f(formData.deedWaterRights)}</div></div>
      </div>

      {/* HOA & Title */}
      <div className="cbs-rv-sec">
        <div className="cbs-rv-head">
          <span className="cbs-rv-head-icon">{STEP_ICONS[8]}</span>
          <span className="cbs-rv-head-title">HOA & Title</span>
        </div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">HOA</div><div className="cbs-rv-val">{f(formData.hoaApplicable)}{formData.hoaName ? ` — ${formData.hoaName}` : ''}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Deed Type</div><div className="cbs-rv-val">{f(formData.deedType)}</div></div>
        <div className="cbs-rv-row"><div className="cbs-rv-lbl">Closing Company</div><div className="cbs-rv-val">{f(formData.closingCompany)}</div></div>
      </div>

      {/* Certification */}
      <div className="cbs-cert">
        <div className="cbs-cert-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          CBS Contract — Agent Confirmation
        </div>
        <div className="cbs-cert-text">
          By generating the Offer Summary, the Buyer's Agent confirms that all information entered is accurate and has been reviewed with the buyer. This wizard data will be used to populate the Colorado Contract to Buy and Sell Real Estate (CBS1) form.
        </div>
        <div className="cbs-two-col">
          <div>
            <label className="rpa-field-label" style={{ color: '#5B47D6', fontSize: 11 }}>Buyer Agent Name</label>
            {inp('agentConfirmName', { style: { borderColor: '#5B47D6' } })}
          </div>
          <div>
            <label className="rpa-field-label" style={{ color: '#5B47D6', fontSize: 11 }}>Date</label>
            <input className="rpa-input" type="date" value={formData.agentConfirmDate} onChange={e => setField('agentConfirmDate', e.target.value)} style={{ borderColor: '#5B47D6' }} />
          </div>
        </div>
      </div>
    </>
  );

  const STEP_RENDERERS = [null, renderStep1, renderStep2, renderStep3, renderStep4,
    renderStep5, renderStep6, renderStep7, renderStep8, renderStep9, renderStep10, renderStep11];

  // ── Render ───────────────────────────────────────────────────────────────
  if (!isOpen) return null;

  const pct = Math.round((completedSteps.length / CBS_TOTAL_STEPS) * 100);
  const propertyLabel = transaction?.propertyAddress || formData.propertyAddress || '';

  return (
    <div className="rpa-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rpa-modal" onClick={e => e.stopPropagation()}>

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="rpa-header">
          <div className="rpa-header-left">
            <div className="rpa-header-title">
              CO Offer Wizard
              <span style={{ fontSize: 13, fontWeight: 500, color: '#888', marginLeft: 8 }}>
                Contract to Buy &amp; Sell Real Estate (CBS)
              </span>
            </div>
            {propertyLabel && (
              <div className="rpa-header-sub">{propertyLabel}</div>
            )}
          </div>
          <button type="button" className="rpa-close-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* ── Progress bar ─────────────────────────────────────────────── */}
        <div className="rpa-progress-track">
          <div className="rpa-progress-bar" style={{ width: `${pct}%` }} />
        </div>

        {isSubmitted ? (
          /* ── Complete screen ──────────────────────────────────────────── */
          <div className="rpa-content cbs-complete">
            <div className="cbs-complete-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <h2>Offer Summary Generated</h2>
            <p>The CBS offer wizard data has been submitted and the offer summary generated. The agent can now create the CBS1 form and send for signature.</p>
            <div className="cbs-complete-btns">
              <button className="rpa-btn rpa-btn-ghost" onClick={onClose}>Return to Transaction</button>
            </div>
          </div>
        ) : (
          <div className="rpa-body">
            {/* ── Stepper ────────────────────────────────────────────────── */}
            <div className="rpa-sidebar">
              {CBS_STEPS.map(step => {
                const isDone   = completedSteps.includes(step.id);
                const isActive = currentStep === step.id;
                const cls = `rpa-step-item${isDone ? ' done' : isActive ? ' active' : ' todo'}`;
                return (
                  <button
                    key={step.id}
                    type="button"
                    className={cls}
                    onClick={() => goToStep(step.id)}
                    title={step.title}
                  >
                    <div className="rpa-step-icon">{STEP_ICONS[step.id]}</div>
                    <div className="rpa-step-meta">
                      <span className="rpa-step-num">{step.label}</span>
                      <span className="rpa-step-title">{step.title}</span>
                      {isDone && <span className="rpa-step-check">✓</span>}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* ── Step content ───────────────────────────────────────────── */}
            <div className="rpa-content">
              <h2 className="rpa-step-heading">
                {CBS_STEPS[currentStep - 1]?.title}
                {currentStep <= 10 && <span className="cbs-co-badge" style={{ marginLeft: 10, fontSize: 11 }}>CO</span>}
              </h2>
              {STEP_RENDERERS[currentStep]?.()}
              {saveError && (
                <div className="rpa-info-box rpa-info-box--danger" style={{ marginTop: 16 }}>
                  {saveError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Footer ──────────────────────────────────────────────────── */}
        {!isSubmitted && (
          <div className="rpa-footer">
            <div className="rpa-footer-left">
              {currentStep > 1 && (
                <button type="button" className="rpa-btn rpa-btn-ghost" onClick={handleBack} disabled={isSaving}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  &nbsp;Back
                </button>
              )}
            </div>
            <div className="rpa-footer-center">
              <span className="rpa-footer-progress">Step {currentStep} of {CBS_TOTAL_STEPS}</span>
            </div>
            <div className="rpa-footer-right">
              {currentStep < CBS_TOTAL_STEPS ? (
                <button
                  type="button"
                  className={`rpa-btn rpa-btn-primary${isSaving ? ' rpa-btn-saving' : ''}`}
                  onClick={handleNext}
                  disabled={isSaving}
                >
                  {isSaving
                    ? <><span className="rpa-btn-spinner" />Saving…</>
                    : <>Next&nbsp;<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></>
                  }
                </button>
              ) : (
                <button
                  type="button"
                  className={`rpa-btn rpa-btn-primary${isSaving ? ' rpa-btn-saving' : ''}`}
                  onClick={handleSubmit}
                  disabled={isSaving}
                >
                  {isSaving
                    ? <><span className="rpa-btn-spinner" />Submitting…</>
                    : <>Generate Offer Summary&nbsp;<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></>
                  }
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
